/**
 * Account management API — GDPR self-service (#531, #532).
 * Feature doc: docs/canonical/features/future/v1.6-gdpr-self-service.md
 */

import { apiGet, apiPost, API_BASE_URL } from '../api';

/**
 * Which channel the confirmation was sent over:
 *   - `email`: a magic link was emailed (real-email accounts). Confirm on the
 *     web `/settings/delete-account/confirm` page via the link.
 *   - `sms`: a 6-digit code was texted (phone-only accounts whose email is the
 *     synthetic {phone}@sms.oro.fit sentinel — #2935). Confirm by submitting
 *     the code to `/users/me/delete-confirm`.
 */
export type DeletionChannel = 'email' | 'sms';

export interface DeleteAccountRequestResponse {
  status: 'confirmation_sent';
  channel: DeletionChannel;
  expires_at: number;
}

export interface DeleteAccountConfirmResponse {
  status: 'deleted';
}

export interface BlockingClub {
  id: string;
  name: string;
  slug: string;
}

export interface BlockedSoleOwnerError {
  error: {
    code: 'BLOCKED_SOLE_CLUB_OWNER';
    message: string;
    blocking_clubs: BlockingClub[];
  };
}

/**
 * GDPR Article 15 export envelope (`GET /users/me/export`).
 *
 * Mirrors the backend `ExportEnvelope` (`backend/src/utils/userDataExport.ts`).
 * Only `export_metadata` is named here — the per-table rows are serialized
 * verbatim and left as an open record rather than duplicating the full per-table
 * interface set (which lives, and is kept honest, on the backend).
 */
export interface DataExportEnvelope {
  export_metadata: {
    version: string;
    generated_at_iso: string;
    generated_at_unix: number;
    user_id: string;
    article: string;
  };
  [section: string]: unknown;
}

/**
 * Native-safe GDPR Article 15 fetch (#2208).
 *
 * The web `accountApi.exportMyData` (below) streams the response to a DOM anchor
 * with cookie auth — neither the blob-anchor nor the cookie jar exists on RN.
 * Native (`mobile/src/hooks/useDataExport.ts`) instead pulls the SAME
 * authenticated endpoint through the shared Bearer transport and hands the
 * parsed envelope to the OS share sheet. `GET /users/me/export` returns
 * `Content-Type: application/json`, so `apiGet` parses it into the envelope
 * object; the hook re-serializes with `JSON.stringify` before writing the file.
 *
 * First mobile consumer of this endpoint — pinned in `mobile-contract.test.ts`.
 */
export async function fetchDataExport(): Promise<DataExportEnvelope> {
  return apiGet<DataExportEnvelope>('/users/me/export');
}

export const accountApi = {
  /**
   * GDPR Article 15 — trigger a JSON download of all user data.
   * Browser handles the file save via Content-Disposition: attachment.
   *
   * Implemented as a direct fetch (rather than apiGet) because the response
   * is a binary download, not a JSON envelope. We still need credentials
   * (cookie auth) and the env-aware base URL.
   */
  exportMyData: async (): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/users/me/export`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      let message = 'Export failed';
      try {
        const body = (await response.json()) as { error?: { message?: string } };
        message = body.error?.message ?? message;
      } catch {
        /* response wasn't JSON */
      }
      throw new Error(message);
    }

    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') ?? '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match?.[1] ?? `oro-data-export-${new Date().toISOString().slice(0, 10)}.json`;

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /**
   * Step 1: Send the account-deletion confirmation email.
   * Returns 409 with `BLOCKED_SOLE_CLUB_OWNER` if the user owns clubs.
   */
  requestDeletion: (): Promise<DeleteAccountRequestResponse> => {
    return apiPost<DeleteAccountRequestResponse>('/users/me/delete-request', {});
  },

  /**
   * Step 2 (email accounts): consume the magic-link token to delete the account.
   */
  confirmDeletion: (token: string): Promise<DeleteAccountConfirmResponse> => {
    return apiPost<DeleteAccountConfirmResponse>('/users/me/delete-confirm', { token });
  },

  /**
   * Step 2 (phone-only accounts, #2935): consume the 6-digit SMS code to delete
   * the account. The backend accepts exactly one of {token, code}.
   */
  confirmDeletionByCode: (code: string): Promise<DeleteAccountConfirmResponse> => {
    return apiPost<DeleteAccountConfirmResponse>('/users/me/delete-confirm', { code });
  },
};
